/**
 * Monitor session replay sampling rates for cost tracking
 */

import { datadogRum } from "@datadog/browser-rum";

interface ReplayMetrics {
  totalSessions: number;
  replaySessions: number;
  errorSessions: number;
  lastReported: number;
}

const STORAGE_KEY = "dd_replay_metrics";
const REPORT_INTERVAL = 24 * 60 * 60 * 1000; // 24 hours

// We intentionally persist replay metrics across tab/browser restarts so we
// can accurately compute daily error/replay rates.
const storage = {
  getItem: (key: string): string | null => {
    try {
      return typeof localStorage !== "undefined"
        ? localStorage.getItem(key)
        : null;
    } catch {
      return null;
    }
  },
  setItem: (key: string, value: string): void => {
    try {
      if (typeof localStorage !== "undefined") localStorage.setItem(key, value);
    } catch {
      // Ignore storage failures (e.g., privacy mode)
    }
  },
};

/**
 * Get current metrics from storage
 */
const getMetrics = (): ReplayMetrics => {
  try {
    const stored = storage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn("Failed to read replay metrics:", error);
  }

  return {
    totalSessions: 0,
    replaySessions: 0,
    errorSessions: 0,
    lastReported: Date.now(),
  };
};

/**
 * Save metrics to storage
 */
const saveMetrics = (metrics: ReplayMetrics): void => {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(metrics));
  } catch (error) {
    console.warn("Failed to save replay metrics:", error);
  }
};

// Track current session state (one per page load)
let currentSessionTracked = false;
let currentSessionHasError = false;
let currentSessionHasReplay = false;

/**
 * Initialize current session tracking (call once per page load)
 */
const initCurrentSession = (): void => {
  currentSessionTracked = false;
  currentSessionHasError = false;
  currentSessionHasReplay = false;
};

/**
 * Mark that current session had an error
 */
export const markSessionError = (): void => {
  currentSessionHasError = true;
};

/**
 * Mark that current session has replay
 */
export const markSessionReplay = (options?: { errored?: boolean }): void => {
  currentSessionHasReplay = true;
  if (options?.errored) {
    currentSessionHasError = true;
  }
};

/**
 * Finalize and save current session metrics
 */
const finalizeCurrentSession = (): void => {
  if (currentSessionTracked) return; // Already finalized

  const metrics = getMetrics();

  // Increment total sessions once
  metrics.totalSessions += 1;

  // Mark error if this session had errors
  if (currentSessionHasError) {
    metrics.errorSessions += 1;
  }

  // Mark replay if this session had replay recording
  if (currentSessionHasReplay) {
    metrics.replaySessions += 1;
  }

  saveMetrics(metrics);
  currentSessionTracked = true;

  // Report if it's been 24 hours
  const shouldReport = Date.now() - metrics.lastReported > REPORT_INTERVAL;
  if (shouldReport && metrics.totalSessions > 0) {
    reportMetrics(metrics);
  }
};

/**
 * Report metrics to analytics
 */
const reportMetrics = (metrics: ReplayMetrics): void => {
  const samplingRate = (metrics.replaySessions / metrics.totalSessions) * 100;
  const errorRate = (metrics.errorSessions / metrics.totalSessions) * 100;

  // Log to console in non-production
  if (import.meta.env.MODE !== "production") {
    console.log("Replay Sampling Metrics:", {
      total_sessions: metrics.totalSessions,
      replay_sessions: metrics.replaySessions,
      error_sessions: metrics.errorSessions,
      replay_sampling_pct: samplingRate.toFixed(2),
      error_rate_pct: errorRate.toFixed(2),
    });
  }

  // Send to PostHog if available
  // Dynamic import to avoid circular dependencies.
  // Note: `try/catch` won't catch promise rejections, so we must handle `.catch(...)`.
  void import("../services/posthogService")
    .then(({ posthog, isPosthogReady }) => {
      if (isPosthogReady()) {
        posthog.capture("replay_sampling_metrics", {
          total_sessions: metrics.totalSessions,
          replay_sessions: metrics.replaySessions,
          error_sessions: metrics.errorSessions,
          replay_sampling_pct: parseFloat(samplingRate.toFixed(2)),
          error_rate_pct: parseFloat(errorRate.toFixed(2)),
          period_hours: 24,
        });
      }
    })
    .catch((error) => {
      console.warn("Failed to report replay metrics to PostHog:", error);
    });

  // Send to Datadog as custom event
  try {
    if (datadogRum.getInitConfiguration()) {
      datadogRum.addAction("replay_sampling_report", {
        total_sessions: metrics.totalSessions,
        replay_sampling_pct: parseFloat(samplingRate.toFixed(2)),
        error_rate_pct: parseFloat(errorRate.toFixed(2)),
      });
    }
  } catch (error) {
    console.warn("Failed to report replay metrics to Datadog:", error);
  }

  // Reset metrics for next period
  metrics.totalSessions = 0;
  metrics.replaySessions = 0;
  metrics.errorSessions = 0;
  metrics.lastReported = Date.now();
  saveMetrics(metrics);
};

/**
 * Initialize replay monitoring
 * Call this once on app start
 */
export const initReplayMonitoring = (): void => {
  // Initialize current session
  initCurrentSession();

  // If a tab is hidden for a long time, treat that as a session boundary.
  const LONG_HIDDEN_FINALIZE_MS = 5 * 60 * 1000;
  let hiddenFinalizeTimeoutId: number | null = null;

  // Mark session as having error when first error occurs
  window.addEventListener(
    "error",
    () => {
      markSessionError();
    },
    { once: true }
  );

  window.addEventListener(
    "unhandledrejection",
    () => {
      markSessionError();
    },
    { once: true }
  );

  // Finalize session on page unload
  window.addEventListener("beforeunload", () => {
    finalizeCurrentSession();
  });

  // Visibility changes can be transient (tab switch). Avoid finalizing immediately on
  // the first "hidden" transition, because users can come back and continue the
  // same session. Instead, finalize only if the tab stays hidden long enough.
  // If we finalized while hidden, start a new session when the tab becomes visible.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      if (hiddenFinalizeTimeoutId == null) {
        hiddenFinalizeTimeoutId = window.setTimeout(() => {
          hiddenFinalizeTimeoutId = null;
          finalizeCurrentSession();
        }, LONG_HIDDEN_FINALIZE_MS);
      }
      return;
    }

    if (document.visibilityState === "visible") {
      if (hiddenFinalizeTimeoutId != null) {
        window.clearTimeout(hiddenFinalizeTimeoutId);
        hiddenFinalizeTimeoutId = null;
      }

      // If we already finalized due to a long-hidden timeout, begin a fresh session.
      if (currentSessionTracked) {
        initCurrentSession();
      }
    }
  });

  // Periodic check to ensure we're tracking and reporting
  const intervalId = setInterval(
    () => {
      const metrics = getMetrics();
      const shouldReport = Date.now() - metrics.lastReported > REPORT_INTERVAL;
      if (shouldReport && metrics.totalSessions > 0) {
        reportMetrics(metrics);
      }
    },
    60 * 60 * 1000
  ); // Check every hour

  // Store interval ID for potential cleanup
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__replayMonitoringInterval = intervalId;
};

/**
 * Get current sampling rate (for debugging)
 */
export const getCurrentSamplingRate = (): number => {
  const metrics = getMetrics();
  if (metrics.totalSessions === 0) return 0;
  return (metrics.replaySessions / metrics.totalSessions) * 100;
};
